import * as React from 'react'

import { cn } from '../lib/utils'
import { TierCard, type TierCardProps } from './tier-card'

/**
 * The plan choice: a `role="radiogroup"` wrapping two (or one) {@link TierCard}s.
 *
 * Owns the three things a native radio group gives you for free and a set of buttons doesn't:
 * **roving tabindex** (one tab stop for the whole group, not one per card), **arrow-key
 * selection**, and a polite live region announcing what's now chosen. Without those, a screen
 * reader hears two unrelated buttons rather than one either/or decision.
 *
 * Nothing is pre-selected in the two-tier case — that's the no-recommendation rule reaching into
 * the markup. The caller decides what "selected" means; this component never defaults it.
 */
export interface TierGroupItem extends Omit<TierCardProps, 'tabbable' | 'onSelect' | 'onKeyDown' | 'selected'> {
  /** Stable key, e.g. the billing tier key. */
  id: string
}

export interface TierGroupProps {
  tiers: TierGroupItem[]
  /** `null` until the user picks. Never defaulted internally. */
  selected: string | null
  onSelect: (id: string) => void
  disabled?: boolean
  /** `aria-label` for the group, e.g. "Choose your plan — 2 options". */
  ariaLabel: string
  /** Announced politely on change, e.g. "Handful selected, $9.99 per month." */
  selectionAnnouncement?: string
  className?: string
}

export const TierGroup = React.forwardRef<HTMLDivElement, TierGroupProps>(
  ({ tiers, selected, onSelect, disabled, ariaLabel, selectionAnnouncement, className }, ref) => {
    const refs = React.useRef<(HTMLButtonElement | null)[]>([])
    const selectedIndex = tiers.findIndex((t) => t.id === selected)

    const move = (from: number, dir: 1 | -1) => {
      const next = (from + dir + tiers.length) % tiers.length
      onSelect(tiers[next].id)
      refs.current[next]?.focus()
    }

    const compactIndex = tiers.findIndex((t) => t.compact)

    return (
      <>
        <div
          ref={ref}
          role="radiogroup"
          aria-label={ariaLabel}
          className={cn(
            'grid items-stretch gap-4 max-[560px]:grid-cols-1 max-[560px]:gap-3.5',
            // The larger card takes 1.07fr against the compact card's 0.93fr, whichever order
            // they're in. One column when there's only one plan to offer.
            tiers.length === 1 && 'grid-cols-[minmax(0,440px)]',
            tiers.length === 2 && compactIndex === 0 && 'grid-cols-[0.93fr_1.07fr]',
            tiers.length === 2 && compactIndex !== 0 && 'grid-cols-[1.07fr_0.93fr]',
            className
          )}>
          {tiers.map((tier, i) => (
            <TierCard
              key={tier.id}
              {...tier}
              selected={selected === tier.id}
              disabled={disabled ?? tier.disabled}
              // Roving tabindex: the selected card owns the tab stop, or the first if none is.
              tabbable={selectedIndex === -1 ? i === 0 : selectedIndex === i}
              onSelect={() => onSelect(tier.id)}
              ref={(el) => {
                refs.current[i] = el
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault()
                  move(i, 1)
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  move(i, -1)
                }
              }}
            />
          ))}
        </div>
        <div aria-live="polite" className="sr-only">
          {selectionAnnouncement ?? ''}
        </div>
      </>
    )
  }
)
TierGroup.displayName = 'TierGroup'
